/* Cart3 header behavior. StickyHeader is copied from Dawn 16 sections/header.liquid (its {% javascript %}
   only loads when Dawn's header section is on the page). Guarded so it never defines twice. */

(() => {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.setHeaderHeight = this.setHeaderHeight.bind(this);
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.mobileMediaQuery = window.matchMedia('(max-width: 990px)');
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.setHeaderHeight();

      this.mobileMediaQuery.addEventListener('change', this.setHeaderHeight);

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearches = this.querySelectorAll('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.mobileMediaQuery.removeEventListener('change', this.setHeaderHeight);
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if ([...this.predictiveSearches].some((predictiveSearch) => predictiveSearch.isOpen)) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('details-modal');
      this.searchModals.forEach((searchModal) => searchModal.close(false));
    }
  }

  // The account `open` event is composed but does not bubble, so listen during capture.
  // Anchor the dialog to the bottom of the account button itself. The header group can be
  // much taller than the button's row (for example, a top-left logo pushes the menu to a
  // second row), so anchoring to the section bottom drops the dialog too low.
  document.addEventListener(
    'open',
    (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;

      const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom));
      event.target.style.setProperty('--account-dialog-top', `${bottom}px`);
    },
    true
  );

  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  // Quick links: CSS shows one row and clips the links that wrap to a second row. Make the clipped
  // links inert so they leave the Tab order and the accessibility tree (focusing one used to scroll
  // the list and push the visible links away). A ResizeObserver on the list and its links re-checks
  // on resize, on the mobile → desktop switch and when the web font swaps in; it fires after layout,
  // so reading offsetTop there is cheap.
  if ('ResizeObserver' in window) {
    const syncQuickLinks = (list) => {
      const first = list.firstElementChild;
      if (!first) return;
      list.scrollTop = 0;
      const top = first.offsetTop;
      [...list.children].forEach((item) => {
        item.inert = item.offsetTop > top;
      });
    };

    const quickLinksObserver = new ResizeObserver((entries) => {
      new Set(entries.map((entry) => entry.target.closest('.c3-header__quick-links'))).forEach(
        (list) => list && syncQuickLinks(list)
      );
    });

    const observeQuickLinks = () => {
      document.querySelectorAll('.c3-header__quick-links:not([data-c3-observed])').forEach((list) => {
        list.dataset.c3Observed = '';
        quickLinksObserver.observe(list);
        [...list.children].forEach((item) => quickLinksObserver.observe(item));
      });
    };

    observeQuickLinks();
    // Theme editor: the header re-renders when a quick link is added, removed or edited.
    document.addEventListener('shopify:section:load', observeQuickLinks);
  }

  // Announcement bar: keep the first message still until the visitor interacts with the page,
  // then let Dawn's rotation run (it keeps pausing on hover/focus and under reduced motion).
  // Avoids late visual changes during load. Skipped in the theme editor.
  const announcementSliders = () =>
    [...document.querySelectorAll('slideshow-component')].filter(
      (component) => component.announcementBarSlider && component.slider?.dataset.autoplay === 'true'
    );

  if (!window.Shopify?.designMode) {
    announcementSliders().forEach((component) => component.pause());

    const startRotation = () => {
      ['scroll', 'pointerdown', 'keydown', 'mousemove', 'touchstart'].forEach((type) =>
        window.removeEventListener(type, startRotation)
      );
      announcementSliders().forEach((component) => {
        if (component.reducedMotion?.matches || component.announcementBarArrowButtonWasClicked) return;
        // Pointer or focus already on the bar: stay paused; Dawn's mouseleave/focusout handlers start it later
        if (component.matches(':hover') || component.contains(document.activeElement)) return;
        component.play();
      });
    };

    ['scroll', 'pointerdown', 'keydown', 'mousemove', 'touchstart'].forEach((type) =>
      window.addEventListener(type, startRotation, { passive: true })
    );
  }
})();
